import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';

import type { SuggestedPrompt } from './useDMSuggestedPromptsData';

const INITIAL_DELAY_MS = 500;
const STAGGER_DELAY_MS = 80;

interface DMSuggestedPromptsProps {
    prompts: SuggestedPrompt[];
    onSelectPrompt: (promptText: string) => void;
}

export default function DMSuggestedPrompts({
    prompts,
    onSelectPrompt
}: DMSuggestedPromptsProps) {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (prompts.length === 0) {
            setIsVisible(false);
            return;
        }
        setIsVisible(false);
        const timer = setTimeout(() => setIsVisible(true), INITIAL_DELAY_MS);
        return () => clearTimeout(timer);
    }, [prompts]);

    if (prompts.length === 0) return null;

    return (
        <div className="top-full left-0 z-10 absolute flex flex-col bg-background w-full">
            {prompts.map((prompt, index) => (
                <Button
                    key={prompt.id}
                    variant="ghost"
                    onClick={() => onSelectPrompt(prompt.prompt)}
                    style={{
                        transitionDelay: isVisible
                            ? `${index * STAGGER_DELAY_MS}ms`
                            : '0ms'
                    }}
                    className={cn(
                        'group justify-between items-start gap-4 px-4 py-6 rounded-none w-full h-auto text-left whitespace-normal',
                        'transition-[opacity,transform] duration-700 ease-out',
                        index < prompts.length - 1 && 'border-b border-border',
                        isVisible
                            ? 'opacity-100 translate-y-0'
                            : 'opacity-0 translate-y-8'
                    )}
                >
                    <div className="flex-1 min-w-0 max-h-8 group-hover:max-h-44 overflow-hidden">
                        <span className="font-normal text-muted-foreground group-hover:text-foreground text-base wrap-break-word line-clamp-1 group-hover:line-clamp-none">
                            {prompt.prompt}
                        </span>
                    </div>
                    <span className="inline-flex justify-center items-center bg-background px-3 border border-border rounded-full h-6 font-medium text-foreground text-xs whitespace-nowrap shrink-0">
                        {prompt.label}
                    </span>
                </Button>
            ))}
        </div>
    );
}
